import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomDropdown from "../../../components/CustomDropdown";
import Sidebar from "../../../components/Sidebar";
import ProfileInfoController from "../../../controller/profileInfoController";
import {
  KycStatus,
  UserDocumentType,
  UserDocumentSubType,
} from "../../../controller/profileInfoController";
import { fetchDocumentImage, getHomePageInfos } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import dropdownOptions from "../../../data/dropdownOptions.json";
import {
  getSubcategoriesForIndustry,
  subcategoryRequiresProfession,
  getSubcategoryLabel,
} from "../../../data/employmentSubcategories";
import "../../css/ProfileInformation.css";

const {
  employmentStatusOptions,
  employmentIndustryOptions,
  educationLevelOptions,
  sourceOfWealthOptions,
  netWorthOptions,
  annualIncomeOptions,
  annualDepositOptions,
  experienceOptions,
  opsPaceOptions,
  tradingStrategyOptions,
  monthlyDepositOptions,
  fundingMethodOptions,
  tradingOnBehalfOfOptions,
} = dropdownOptions;

function findOption(options, value) {
  if (value == null || Array.isArray(value)) return null;
  return (
    options.find(
      (o) =>
        o.value === value ||
        o.value === Number(value) ||
        String(o.value) === String(value)
    ) || null
  );
}

export default function UserRegulation() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [activeSection, setActiveSection] = useState("employment");

  // Collapsible sections (accordion)
  const [expandedSections, setExpandedSections] = useState({
    employment: true,
    income: false,
    trading: false,
    experience: false,
    activity: false,
  });

  // Regulation fields
  const [taxId, setTaxId] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState(null);
  const [employmentIndustry, setEmploymentIndustry] = useState(null);
  const [employmentSubcategory, setEmploymentSubcategory] = useState(null);
  const [profession, setProfession] = useState("");
  const [educationLevel, setEducationLevel] = useState(null);
  const [sourceOfWealth, setSourceOfWealth] = useState(null);
  const [netWorth, setNetWorth] = useState(null);
  const [annualIncome, setAnnualIncome] = useState(null);
  const [annualDeposit, setAnnualDeposit] = useState(null);
  const [tradingStrategy, setTradingStrategy] = useState(null);
  const [tradingStrategyOther, setTradingStrategyOther] = useState("");
  const [monthlyDeposit, setMonthlyDeposit] = useState(null);
  const [fundingMethod, setFundingMethod] = useState(null);
  const [fundingMethodOther, setFundingMethodOther] = useState("");
  const [depositsFromOwnName, setDepositsFromOwnName] = useState(null);
  const [tradingForYourself, setTradingForYourself] = useState(null);
  const [tradingOnBehalfOf, setTradingOnBehalfOf] = useState(null);
  const [tradingOnBehalfOther, setTradingOnBehalfOther] = useState("");
  const [experienceForex, setExperienceForex] = useState(null);
  const [experienceStocks, setExperienceStocks] = useState(null);
  const [experienceFutures, setExperienceFutures] = useState(null);
  const [experienceCfd, setExperienceCfd] = useState(null);
  const [opsPace, setOpsPace] = useState(null);
  const [avgTxAmount, setAvgTxAmount] = useState("");
  const [expectedAnnualActivity, setExpectedAnnualActivity] = useState("");

  const [errors, setErrors] = useState({});

  const kycStatus = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return (
      info.IS_KYC_VERIFIED ?? info.isKycVerified ?? info.is_kyc_verified ?? 0
    );
  }, [profileData]);

  const canEdit =
    kycStatus !== KycStatus.VERIFIED &&
    kycStatus !== KycStatus.VERIFIED_NOT_CHECKED;

  const kycComments = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return info.COMMENTS ?? info.comments ?? "";
  }, [profileData]);

  const kycStatusText = useMemo(() => {
    if (
      kycStatus === KycStatus.VERIFIED ||
      kycStatus === KycStatus.VERIFIED_NOT_CHECKED
    )
      return t("verified", { ns: "mobile" });
    return t("not_verified", { ns: "mobile" });
  }, [kycStatus, t]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const populateFields = useCallback((data) => {
    setTaxId(data.taxId || "");
    setEmploymentStatus(findOption(employmentStatusOptions, data.employmentStatus));
    setEmploymentIndustry(findOption(employmentIndustryOptions, data.employmentIndustry));
    const subOpts = getSubcategoriesForIndustry(data.employmentIndustry, data.employmentStatus);
    setEmploymentSubcategory(findOption(subOpts, data.employmentSubcategory));
    setProfession(data.profession || "");
    setEducationLevel(findOption(educationLevelOptions, data.educationLevel));
    setSourceOfWealth(findOption(sourceOfWealthOptions, data.sourceOfWealth));
    setNetWorth(findOption(netWorthOptions, data.netWorth));
    setAnnualIncome(findOption(annualIncomeOptions, data.annualIncome));
    setAnnualDeposit(findOption(annualDepositOptions, data.annualDeposit));
    setTradingStrategy(findOption(tradingStrategyOptions, data.tradingStrategy));
    setTradingStrategyOther(data.tradingStrategyOther || "");
    setMonthlyDeposit(findOption(monthlyDepositOptions, data.monthlyDeposit));
    const firstFunding = Array.isArray(data.fundingMethods)
      ? (data.fundingMethods.length > 0 ? data.fundingMethods[0] : null)
      : data.fundingMethods;
    setFundingMethod(findOption(fundingMethodOptions, firstFunding));
    setFundingMethodOther(data.fundingMethodOther || "");
    setDepositsFromOwnName(
      data.depositsFromOwnName != null ? Number(data.depositsFromOwnName) : null
    );
    setTradingForYourself(
      data.tradingForYourself != null ? Number(data.tradingForYourself) : null
    );
    setTradingOnBehalfOf(findOption(tradingOnBehalfOfOptions, data.tradingOnBehalfOf));
    setTradingOnBehalfOther(data.tradingOnBehalfOther || "");
    setExperienceForex(findOption(experienceOptions, data.experienceForex));
    setExperienceStocks(findOption(experienceOptions, data.experienceStocks));
    setExperienceFutures(findOption(experienceOptions, data.experienceFutures));
    setExperienceCfd(findOption(experienceOptions, data.experienceCfd));
    setOpsPace(findOption(opsPaceOptions, data.opsPace));
    setAvgTxAmount(data.avgTxAmount != null ? String(data.avgTxAmount) : "");
    setExpectedAnnualActivity(data.expectedAnnualActivity != null ? String(data.expectedAnnualActivity) : "");
  }, []);

  const subcategoryOptions = useMemo(
    () =>
      getSubcategoriesForIndustry(
        employmentIndustry?.value,
        employmentStatus?.value
      ),
    [employmentIndustry, employmentStatus]
  );

  const showProfessionInput = subcategoryRequiresProfession(employmentSubcategory?.value);
  const showTradingStrategyOther = tradingStrategy?.value === 4;
  const showFundingMethodOther = fundingMethod?.value === 6;
  const showTradingOnBehalfOf = tradingForYourself === 0;
  const showTradingOnBehalfOther = showTradingOnBehalfOf && tradingOnBehalfOf?.value === 4;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let userDataFromStorage = null;
      try {
        const storedUserData = sessionStorage.getItem("userData");
        const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
        if (storedUserData && storedTimestamp) {
          const age = Date.now() - parseInt(storedTimestamp, 10);
          if (age < 5 * 60 * 1000) {
            userDataFromStorage = JSON.parse(storedUserData);
            setUser(userDataFromStorage);
          }
        }
      } catch (_) {}

      if (!userDataFromStorage) {
        try {
          const homeResp = await getHomePageInfos();
          const homeData = homeResp?.decryptedData || homeResp?.data;
          if (
            homeData?.CODE === AppConstants.responseOk &&
            homeData?.DATA
          ) {
            const ud = homeData.DATA;
            setUser(ud);
            try {
              sessionStorage.setItem("userData", JSON.stringify(ud));
              sessionStorage.setItem("userDataTimestamp", String(Date.now()));
            } catch (_) {}
          }
        } catch (_) {}
      }

      const result = await ProfileInfoController.getProfileInfo({ withDoc: 0 });
      if (result.success && result.data) {
        setProfileData(result.data);
        populateFields(result.data);
      } else {
        setError(result.error || "Failed to load regulation data");
      }
    } catch (err) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [populateFields]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobileView) return;
    setExpandedSections({
      employment: activeSection === "employment",
      income: activeSection === "income",
      trading: activeSection === "trading",
      experience: activeSection === "experience",
      activity: activeSection === "activity",
    });
  }, [activeSection, isMobileView]);

  useEffect(() => {
    let cancelled = false;
    const loadHeaderImage = async () => {
      try {
        if (!profileData?.raw) {
          setAvatarUrl(null);
          return;
        }
        const docInfo = ProfileInfoController.extractDocumentUrls(profileData.raw);
        const imagePath = docInfo.profile || null;
        if (!imagePath) {
          setAvatarUrl(null);
          return;
        }
        const resolved = await fetchDocumentImage(imagePath);
        if (!cancelled) {
          setAvatarUrl(resolved || null);
        }
      } catch (_) {
        if (!cancelled) setAvatarUrl(null);
      }
    };
    loadHeaderImage();
    return () => {
      cancelled = true;
    };
  }, [profileData]);

  // ---------- TOGGLE SECTION (accordion) ----------
  const toggleSection = (key) => {
    if (!isMobileView) {
      setActiveSection(key);
      return;
    }
    setExpandedSections((prev) => {
      const isOpen = prev[key];
      const allClosed = { employment: false, income: false, trading: false, experience: false, activity: false };
      return isOpen ? allClosed : { ...allClosed, [key]: true };
    });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    if (profileData) {
      populateFields(profileData);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!taxId || taxId.trim() === "") newErrors.taxId = "Tax ID is required";
    if (!employmentStatus) newErrors.employmentStatus = "Employment Status is required";
    if (!employmentIndustry) newErrors.employmentIndustry = "Employment Industry is required";
    if (!employmentSubcategory) newErrors.employmentSubcategory = "Profession is required";
    if (showProfessionInput && (!profession || !profession.trim())) {
      newErrors.profession = "Please specify your exact occupation, job title or profession";
    }
    if (!educationLevel) newErrors.educationLevel = "Education Level is required";
    if (!sourceOfWealth) newErrors.sourceOfWealth = "Source of Wealth is required";
    if (!netWorth) newErrors.netWorth = "Net Worth is required";
    if (!annualIncome) newErrors.annualIncome = "Annual Income is required";
    if (!annualDeposit) newErrors.annualDeposit = "Annual Deposit is required";
    if (!tradingStrategy) newErrors.tradingStrategy = "Trading Strategy is required";
    if (showTradingStrategyOther && (!tradingStrategyOther || !tradingStrategyOther.trim())) {
      newErrors.tradingStrategyOther = "Please specify your trading strategy";
    }
    if (!monthlyDeposit) newErrors.monthlyDeposit = "Monthly Deposit is required";
    if (!fundingMethod) {
      newErrors.fundingMethods = "Funding Method is required";
    }
    if (showFundingMethodOther && (!fundingMethodOther || !fundingMethodOther.trim())) {
      newErrors.fundingMethodOther = "Please specify your funding method";
    }
    if (depositsFromOwnName === null || depositsFromOwnName === undefined) {
      newErrors.depositsFromOwnName = "Please answer this question";
    }
    if (tradingForYourself === null || tradingForYourself === undefined) {
      newErrors.tradingForYourself = "Please answer this question";
    }
    if (showTradingOnBehalfOf && !tradingOnBehalfOf) {
      newErrors.tradingOnBehalfOf = "Please select on whose behalf you are trading";
    }
    if (showTradingOnBehalfOther && (!tradingOnBehalfOther || !tradingOnBehalfOther.trim())) {
      newErrors.tradingOnBehalfOther = "Please specify";
    }
    if (!experienceForex) newErrors.experienceForex = "Forex Experience is required";
    if (!experienceStocks) newErrors.experienceStocks = "Stocks Experience is required";
    if (!experienceFutures) newErrors.experienceFutures = "Futures Experience is required";
    if (!experienceCfd) newErrors.experienceCfd = "CFD Experience is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setError(null);

    try {
      const userId = profileData?.id || profileData?.raw?.ID;

      // Start from the existing raw regulation data (preserves USER_ID, CREATED_AT, UPDATED_AT, etc.)
      // then overlay the user's edits — matches Flutter's _userData!.userRegulation?.toJson()
      const rawReg =
        profileData?.raw?.user_regulation ||
        profileData?.raw?.USER_REGULATION ||
        profileData?.raw?.userRegulation ||
        {};
      const userRegulation = {
        ...rawReg,
        USER_ID: userId,
        TAX_ID: taxId.trim(),
        EMPLOYMENT_STATUS: employmentStatus?.value ?? null,
        EMPLOYMENT_INDUSTRY: employmentIndustry?.value ?? null,
        EMPLOYMENT_SUBCATEGORY: employmentSubcategory?.value ?? null,
        EDUCATION_LEVEL: educationLevel?.value ?? null,
        SOURCE_OF_WEALTH: sourceOfWealth?.value ?? null,
        NET_WORTH: netWorth?.value ?? null,
        ANNUAL_INCOME: annualIncome?.value ?? null,
        ANNUAL_DEPOSIT: annualDeposit?.value ?? null,
        TRADING_STRATEGY: tradingStrategy?.value ?? null,
        TRADING_STRATEGY_OTHER: showTradingStrategyOther ? tradingStrategyOther.trim() : null,
        MONTHLY_DEPOSIT: monthlyDeposit?.value ?? null,
        FUNDING_METHODS: fundingMethod?.value != null ? [fundingMethod.value] : [],
        FUNDING_METHOD_OTHER: showFundingMethodOther ? fundingMethodOther.trim() : null,
        DEPOSITS_FROM_OWN_NAME: depositsFromOwnName,
        TRADING_FOR_YOURSELF: tradingForYourself,
        TRADING_ON_BEHALF_OF: showTradingOnBehalfOf ? (tradingOnBehalfOf?.value ?? null) : null,
        TRADING_ON_BEHALF_OTHER: showTradingOnBehalfOther ? tradingOnBehalfOther.trim() : null,
        EXPERIENCE_FOREX: experienceForex?.value ?? null,
        EXPERIENCE_STOCKS: experienceStocks?.value ?? null,
        EXPERIENCE_FUTURES: experienceFutures?.value ?? null,
        EXPERIENCE_CFD: experienceCfd?.value ?? null,
        OPS_PACE: opsPace?.value ?? rawReg.OPS_PACE ?? null,
        AVG_TX_AMOUNT: avgTxAmount ? parseFloat(avgTxAmount) : (rawReg.AVG_TX_AMOUNT ?? null),
        EXPECTED_ANNUAL_ACTIVITY: expectedAnnualActivity ? parseFloat(expectedAnnualActivity) : (rawReg.EXPECTED_ANNUAL_ACTIVITY ?? null),
      };

      const rawInfo =
        profileData?.raw?.user_information ||
        profileData?.raw?.USER_INFORMATION ||
        {};
      const resolvedProfession = showProfessionInput
        ? profession.trim()
        : (getSubcategoryLabel(employmentSubcategory?.value) || profession.trim() || "");
      const userInformation = {
        ...rawInfo,
        PROFESSION: resolvedProfession || rawInfo.PROFESSION || null,
      };

      // Fetch document data (withDoc: 1) needed for the save request
      // The backend requires USER_DOCUMENTS and related fields alongside regulation data
      let userDocuments = [];
      let selectedDocSubTypes = {};
      try {
        const docResult = await ProfileInfoController.getProfileInfo({ withDoc: 1 });
        if (docResult.success && docResult.data?.raw) {
          const docInfo = ProfileInfoController.extractDocumentUrls(docResult.data.raw);
          userDocuments = docInfo.rawDocuments || [];
          selectedDocSubTypes = {
            [UserDocumentType.PROOF_OF_IDENTITY]: docInfo.identityDocSubType ?? null,
            [UserDocumentType.PROOF_OF_ADDRESS]: docInfo.addressDocSubType ?? null,
          };
        }
      } catch (e) {
        console.error("[UserRegulation] Failed to fetch doc data:", e);
      }

      const result = await ProfileInfoController.saveRegulation({
        userId,
        userInformation,
        userRegulation,
        userDocuments,
        selectedDocSubTypes,
      });

      if (result.success) {
        setSuccess("Regulation information saved successfully!");
        setIsEditMode(false);
        await fetchData();
      } else {
        setError(result.error || "Failed to save regulation data");
      }
    } catch (err) {
      setError(err.message || "An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };

  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: null }));
    }
  };

  const isVerified =
    kycStatus === KycStatus.VERIFIED ||
    kycStatus === KycStatus.VERIFIED_NOT_CHECKED;
  const resolvedFirstName =
    profileData?.firstName ||
    user?.F_NAME ||
    user?.f_name ||
    user?.first_name ||
    "";
  const resolvedLastName =
    profileData?.lastName ||
    user?.L_NAME ||
    user?.l_name ||
    user?.last_name ||
    "";
  const displayName =
    [resolvedFirstName, resolvedLastName].filter(Boolean).join(" ").trim() ||
    "User";

  // ---------- HELPERS ----------
  const isDisabled = !isEditMode || !canEdit;

  const renderField = (label, icon, value, onChange, opts = {}) => {
    const { type = "text", placeholder, required: isRequired, step, min, errorKey } = opts;
    return (
      <div className="profile-form-group">
        <label className="profile-form-label">
          {label}
          {isRequired && <span className="profile-required-star">*</span>}
        </label>
        <div className="profile-input-wrapper">
          <span className="profile-input-icon">
            <i className={`fa-solid ${icon}`}></i>
          </span>
          <input
            type={type}
            step={step}
            min={min}
            className={`profile-input ${errorKey && errors[errorKey] ? "profile-input--error" : ""}`}
            placeholder={placeholder || label}
            value={value}
            disabled={isDisabled || saving}
            readOnly={isDisabled}
            onChange={(e) => {
              onChange(e.target.value);
              if (errorKey) clearError(errorKey);
            }}
          />
        </div>
        {errorKey && errors[errorKey] && (
          <span style={{ color: "#E74A3B", fontSize: 12, marginTop: 2 }}>
            {errors[errorKey]}
          </span>
        )}
      </div>
    );
  };

  const renderDropdown = (label, options, value, onChange, opts = {}) => {
    const { required: isRequired, errorKey, remountKey } = opts;
    return (
      <div className="profile-form-group">
        <label className="profile-form-label">
          {label}
          {isRequired && <span className="profile-required-star">*</span>}
        </label>
        <CustomDropdown
          key={remountKey}
          options={options}
          value={value}
          onChange={(v) => {
            onChange(v);
            if (errorKey) clearError(errorKey);
          }}
          placeholder={label}
          className={`profile-dropdown ${errorKey && errors[errorKey] ? "profile-input--error" : ""}`}
          disabled={isDisabled || saving}
        />
        {errorKey && errors[errorKey] && (
          <span style={{ color: "#E74A3B", fontSize: 12, marginTop: 2 }}>
            {errors[errorKey]}
          </span>
        )}
      </div>
    );
  };

  const renderCheckboxGroup = (label, options, selectedValues, onToggle, opts = {}) => {
    const { required: isRequired, errorKey } = opts;
    return (
      <div className="profile-form-group profile-form-group--full">
        <label className="profile-form-label">
          {label}
          {isRequired && <span className="profile-required-star">*</span>}
        </label>
        <div className={`profile-checkbox-group ${errorKey && errors[errorKey] ? "profile-checkbox-group--error" : ""}`}>
          {options.map((opt) => (
            <label className="profile-checkbox-row" key={opt.value}>
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.value)}
                disabled={isDisabled || saving}
                onChange={() => onToggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {errorKey && errors[errorKey] && (
          <span style={{ color: "#E74A3B", fontSize: 12, marginTop: 2 }}>
            {errors[errorKey]}
          </span>
        )}
      </div>
    );
  };

  const renderYesNoRadio = (label, value, onChange, opts = {}) => {
    const { required: isRequired, errorKey } = opts;
    return (
      <div className="profile-form-group profile-form-group--full">
        <label className="profile-form-label">
          {label}
          {isRequired && <span className="profile-required-star">*</span>}
        </label>
        <div className="profile-radio-group">
          <label className="radio-option">
            <input
              type="radio"
              checked={value === 0}
              disabled={isDisabled || saving}
              onChange={() => {
                onChange(0);
                if (errorKey) clearError(errorKey);
              }}
            />
            <span className="custom-radio" />
            <span className="radio-text">{t("no", { ns: "mobile" })}</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              checked={value === 1}
              disabled={isDisabled || saving}
              onChange={() => {
                onChange(1);
                if (errorKey) clearError(errorKey);
              }}
            />
            <span className="custom-radio" />
            <span className="radio-text">{t("yes", { ns: "mobile" })}</span>
          </label>
        </div>
        {errorKey && errors[errorKey] && (
          <span style={{ color: "#E74A3B", fontSize: 12, marginTop: 2 }}>
            {errors[errorKey]}
          </span>
        )}
      </div>
    );
  };

  // ---------- SECTION HEADER ----------
  const renderSectionHeader = (title, sectionKey) => (
    <div
      className="profile-section-header"
      onClick={() => toggleSection(sectionKey)}
    >
      <h3 className="profile-section-title profile-section-title--collapsible">
        {title}
      </h3>
      <i
        className={`fa-solid ${
          expandedSections[sectionKey] ? "fa-chevron-up" : "fa-chevron-down"
        } profile-section-chevron`}
      ></i>
    </div>
  );

  // ---------- SECTION 1: Financial & Employment ----------
  const renderEmploymentSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("financial_employment_details", { ns: "mobile" }), "employment")}
      {expandedSections.employment && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderField(t("tax_id", { ns: "mobile" }), "fa-id-card", taxId, setTaxId, {
              required: true,
              errorKey: "taxId",
              placeholder: t("enter_your_tax_id", { ns: "mobile" }),
            })}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("employment_status", { ns: "mobile" }),
              employmentStatusOptions,
              employmentStatus,
              (v) => {
                setEmploymentStatus(v);
                setEmploymentSubcategory(null);
                setProfession("");
              },
              { required: true, errorKey: "employmentStatus" }
            )}
            {renderDropdown(
              t("employment_industry", { ns: "mobile" }),
              employmentIndustryOptions,
              employmentIndustry,
              (v) => {
                setEmploymentIndustry(v);
                setEmploymentSubcategory(null);
                setProfession("");
              },
              { required: true, errorKey: "employmentIndustry" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("employment_subcategory", { ns: "mobile" }) !== "employment_subcategory"
                ? t("employment_subcategory", { ns: "mobile" })
                : "Profession",
              subcategoryOptions,
              employmentSubcategory &&
                subcategoryOptions.some((o) => o.value === employmentSubcategory.value)
                ? employmentSubcategory
                : null,
              (v) => {
                setEmploymentSubcategory(v);
                if (v && !subcategoryRequiresProfession(v.value)) {
                  setProfession(v.label || getSubcategoryLabel(v.value) || "");
                } else {
                  setProfession("");
                }
              },
              {
                required: true,
                errorKey: "employmentSubcategory",
                remountKey: `subcat-${employmentIndustry?.value ?? "x"}-${employmentStatus?.value ?? "x"}`,
              }
            )}
            {showProfessionInput &&
              renderField(
                t("profession", { ns: "mobile" }) !== "profession"
                  ? t("profession", { ns: "mobile" })
                  : "Specify",
                "fa-briefcase",
                profession,
                setProfession,
                {
                  required: true,
                  errorKey: "profession",
                  placeholder:
                    "Please specify your exact occupation, job title or profession",
                }
              )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("education_level", { ns: "mobile" }),
              educationLevelOptions,
              educationLevel,
              setEducationLevel,
              { required: true, errorKey: "educationLevel" }
            )}
            {renderDropdown(
              t("source_of_wealth", { ns: "mobile" }),
              sourceOfWealthOptions,
              sourceOfWealth,
              setSourceOfWealth,
              { required: true, errorKey: "sourceOfWealth" }
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- SECTION 2: Net Worth & Income ----------
  const renderIncomeSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("net_worth_income", { ns: "mobile" }), "income")}
      {expandedSections.income && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderDropdown(
              t("net_worth", { ns: "mobile" }),
              netWorthOptions,
              netWorth,
              setNetWorth,
              { required: true, errorKey: "netWorth" }
            )}
            {renderDropdown(
              t("annual_income", { ns: "mobile" }),
              annualIncomeOptions,
              annualIncome,
              setAnnualIncome,
              { required: true, errorKey: "annualIncome" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("annual_deposit", { ns: "mobile" }),
              annualDepositOptions,
              annualDeposit,
              setAnnualDeposit,
              { required: true, errorKey: "annualDeposit" }
            )}
            {renderDropdown(
              t("operations_pace", { ns: "mobile" }),
              opsPaceOptions,
              opsPace,
              setOpsPace,
              {}
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- SECTION 2b: Trading & Funding (KYC) ----------
  const renderTradingSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("trading_funding_details", { ns: "mobile" }), "trading")}
      {expandedSections.trading && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderDropdown(
              t("trading_strategy", { ns: "mobile" }),
              tradingStrategyOptions,
              tradingStrategy,
              (v) => {
                setTradingStrategy(v);
                if (v?.value !== 4) setTradingStrategyOther("");
              },
              { required: true, errorKey: "tradingStrategy" }
            )}
            {showTradingStrategyOther &&
              renderField(
                t("trading_strategy_other", { ns: "mobile" }),
                "fa-pen",
                tradingStrategyOther,
                setTradingStrategyOther,
                {
                  required: true,
                  errorKey: "tradingStrategyOther",
                  placeholder: t("please_specify", { ns: "mobile" }),
                }
              )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("monthly_deposit", { ns: "mobile" }),
              monthlyDepositOptions,
              monthlyDeposit,
              setMonthlyDeposit,
              { required: true, errorKey: "monthlyDeposit" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("funding_methods", { ns: "mobile" }),
              fundingMethodOptions,
              fundingMethod,
              (v) => {
                setFundingMethod(v);
                if (v?.value !== 6) setFundingMethodOther("");
              },
              { required: true, errorKey: "fundingMethods" }
            )}
          </div>
          {showFundingMethodOther && (
            <div className="profile-form-row">
              {renderField(
                t("funding_method_other", { ns: "mobile" }),
                "fa-pen",
                fundingMethodOther,
                setFundingMethodOther,
                {
                  required: true,
                  errorKey: "fundingMethodOther",
                  placeholder: t("please_specify", { ns: "mobile" }),
                }
              )}
            </div>
          )}
          <div className="profile-form-row">
            {renderYesNoRadio(
              t("deposits_from_own_name", { ns: "mobile" }),
              depositsFromOwnName,
              setDepositsFromOwnName,
              { required: true, errorKey: "depositsFromOwnName" }
            )}
          </div>
          <div className="profile-form-row">
            {renderYesNoRadio(
              t("trading_for_yourself", { ns: "mobile" }),
              tradingForYourself,
              (v) => {
                setTradingForYourself(v);
                if (v === 1) {
                  setTradingOnBehalfOf(null);
                  setTradingOnBehalfOther("");
                }
              },
              { required: true, errorKey: "tradingForYourself" }
            )}
          </div>
          {showTradingOnBehalfOf && (
            <>
              <div className="profile-form-row">
                <div className="profile-form-group profile-form-group--full">
                  <span className="profile-field-note">
                    {t("trading_on_behalf_note", { ns: "mobile" })}
                  </span>
                </div>
              </div>
              <div className="profile-form-row">
                {renderDropdown(
                  t("trading_on_behalf_of", { ns: "mobile" }),
                  tradingOnBehalfOfOptions,
                  tradingOnBehalfOf,
                  (v) => {
                    setTradingOnBehalfOf(v);
                    if (v?.value !== 4) setTradingOnBehalfOther("");
                  },
                  { required: true, errorKey: "tradingOnBehalfOf" }
                )}
                {showTradingOnBehalfOther &&
                  renderField(
                    t("trading_on_behalf_other", { ns: "mobile" }),
                    "fa-pen",
                    tradingOnBehalfOther,
                    setTradingOnBehalfOther,
                    {
                      required: true,
                      errorKey: "tradingOnBehalfOther",
                      placeholder: t("please_specify", { ns: "mobile" }),
                    }
                  )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  // ---------- SECTION 3: Trading Experience ----------
  const renderExperienceSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("trading_experience", { ns: "mobile" }), "experience")}
      {expandedSections.experience && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderDropdown(
              t("forex_experience", { ns: "mobile" }),
              experienceOptions,
              experienceForex,
              setExperienceForex,
              { required: true, errorKey: "experienceForex" }
            )}
            {renderDropdown(
              t("stocks_experience", { ns: "mobile" }),
              experienceOptions,
              experienceStocks,
              setExperienceStocks,
              { required: true, errorKey: "experienceStocks" }
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              t("futures_experience", { ns: "mobile" }),
              experienceOptions,
              experienceFutures,
              setExperienceFutures,
              { required: true, errorKey: "experienceFutures" }
            )}
            {renderDropdown(
              t("cfd_experience", { ns: "mobile" }),
              experienceOptions,
              experienceCfd,
              setExperienceCfd,
              { required: true, errorKey: "experienceCfd" }
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- SECTION 4: Transaction ----------
  const renderActivitySection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("transaction", { ns: "mobile" }), "activity")}
      {expandedSections.activity && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderField(
              t("average_transaction_amount", { ns: "mobile" }),
              "fa-dollar-sign",
              avgTxAmount,
              setAvgTxAmount,
              {
                type: "number",
                step: "0.01",
                min: "0",
                placeholder: t("enter_average_transaction_amount", { ns: "mobile" }),
              }
            )}
            {renderField(
              t("expected_annual_activity", { ns: "mobile" }),
              "fa-chart-line",
              expectedAnnualActivity,
              setExpectedAnnualActivity,
              {
                type: "number",
                step: "0.01",
                min: "0",
                placeholder: t("enter_expected_annual_activity", { ns: "mobile" }),
              }
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- RENDER ----------
  return (
    <div className="profile-information-page user-regulation-page">
      <Sidebar user={user} />
      <div className="profile-information-content">
        {error && (
          <div className="profile-error-message">
            <strong>{t("error", { ns: "mobile" })}:</strong> {error}
          </div>
        )}
        {success && (
          <div className="profile-success-message">
            <strong>{t("success", { ns: "mobile" })}:</strong> {success}
          </div>
        )}
        {loading && (
          <div className="profile-loading-message">
            {t("loading_regulation_data", { ns: "mobile" })}
          </div>
        )}

        {!loading && profileData && (
          <div className="profile-information-container profile-information-container--plain" style={{ position: "relative" }}>
            {/* Edit / Cancel Button */}
            {!loading && canEdit && !isEditMode && (
              <button
                type="button"
                className="profile-edit-btn profile-edit-btn--card"
                onClick={() => setIsEditMode(true)}
              >
                <i className="fa-solid fa-pen-to-square"></i>
                {t("edit", { ns: "mobile" })}
              </button>
            )}
            {isEditMode && (
              <button
                type="button"
                className="profile-cancel-edit-btn profile-cancel-edit-btn--card"
                onClick={handleCancelEdit}
              >
                <i className="fa-solid fa-xmark"></i>
                {t("cancel", { ns: "mobile" })}
              </button>
            )}

            {/* Header */}
            <div className="profile-avatar-section">
              <div className="profile-avatar-circle">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="profile-avatar-img"
                    onError={(e) => {
                      e.target.style.display = "none";
                      if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="profile-avatar-fallback"
                  style={{ display: avatarUrl ? "none" : "flex" }}
                >
                  <i className="fa-solid fa-user"></i>
                </div>
              </div>
              <div className="profile-avatar-info">
                <span className="profile-avatar-name">{displayName}</span>
                <span
                  className={`profile-avatar-status ${
                    isVerified
                      ? "profile-avatar-status--verified"
                      : "profile-avatar-status--unverified"
                  }`}
                >
                  <i
                    className={`fa-solid ${
                      isVerified ? "fa-circle-check" : "fa-circle-exclamation"
                    }`}
                  ></i>
                  {kycStatusText}
                </span>
              </div>
            </div>

            {/* KYC Comments */}
            {kycStatus !== KycStatus.VERIFIED && kycComments && (
              <div className="profile-kyc-comments">{kycComments}</div>
            )}

            {/* Desktop horizontal section switcher */}
            {!isMobileView && (
              <div className="profile-sections-swiper" role="tablist" aria-label="Regulation sections">
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "employment" ? "active" : ""}`}
                  onClick={() => setActiveSection("employment")}
                >
                  {t("financial_employment_details", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "income" ? "active" : ""}`}
                  onClick={() => setActiveSection("income")}
                >
                  {t("net_worth_income", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "trading" ? "active" : ""}`}
                  onClick={() => setActiveSection("trading")}
                >
                  {t("trading_funding_details", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "experience" ? "active" : ""}`}
                  onClick={() => setActiveSection("experience")}
                >
                  {t("trading_experience", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "activity" ? "active" : ""}`}
                  onClick={() => setActiveSection("activity")}
                >
                  {t("transaction", { ns: "mobile" })}
                </button>
              </div>
            )}

            {/* Sections: mobile keeps collapsible, desktop shows active section */}
            {isMobileView ? (
              <>
                {renderEmploymentSection()}
                {renderIncomeSection()}
                {renderTradingSection()}
                {renderExperienceSection()}
                {renderActivitySection()}
              </>
            ) : (
              <>
                {activeSection === "employment" && renderEmploymentSection()}
                {activeSection === "income" && renderIncomeSection()}
                {activeSection === "trading" && renderTradingSection()}
                {activeSection === "experience" && renderExperienceSection()}
                {activeSection === "activity" && renderActivitySection()}
              </>
            )}

            {/* Save Button */}
            {isEditMode && (
              <div className="profile-nav-buttons profile-nav-buttons--single">
                <button
                  className="profile-btn-save"
                  onClick={handleSave}
                  disabled={saving}
                  type="button"
                >
                  {saving ? (
                    t("saving", { ns: "mobile" }) + "..."
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i>
                      {t("save_changes", { ns: "mobile" })}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
